import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import BuyerEditModal from '../../components/buyers/BuyerEditModal';
import BankDetailsTab from '../../components/buyers/buyerDetails/tabs/BankDetailsTab';
import MaterialsTab from '../../components/buyers/buyerDetails/tabs/MaterialsTab';
import ContactsTab from '../../components/buyers/buyerDetails/tabs/ContactsTab';
import StatusEditModal from '../../components/buyers/BuyerStatusEditModal';
import { PulseLoader } from "react-spinners";
import { Table, Pagination, Form, Row, Col, Button, Badge, Card, Alert } from 'react-bootstrap';
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';

function BuyerDetail() {
    const { id } = useParams();
    const [buyer, setBuyer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    // const [showProductsModal, setShowProductsModal] = useState(false);
    const [activeTab, setActiveTab] = useState('contacts');
    const [showStatusModal, setShowStatusModal] = useState(false);

    // Sales Orders state
    const [salesOrders, setSalesOrders] = useState([]);
    const [soLoading, setSoLoading] = useState(false);
    const [soPagination, setSoPagination] = useState({});
    const [soFilters, setSoFilters] = useState({
        page: 1,
        limit: 10,
        status: '',
        startDate: '',
        endDate: '',
        soNumber: '',
        brokerName: ''
    });

    // Permission checks
    const canViewBuyers = useHasPermission(PERMISSIONS.VIEW_BUYERS);
    const canEditBuyers = useHasPermission(PERMISSIONS.EDIT_BUYERS);

    const fetchBuyer = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/buyers/${id}`);
            setBuyer(res.data);
        } catch (err) {
            console.error("Failed to fetch buyer", err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchSalesOrders = useCallback(async () => {
        setSoLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(soFilters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });

            const res = await axios.get(`${config.API_BASE_URL}/buyers/${id}/sales-orders?${params}`);
            setSalesOrders(res.data.salesOrders);
            setSoPagination(res.data.pagination);
        } catch (err) {
            console.error("Failed to fetch sales orders", err);
        } finally {
            setSoLoading(false);
        }
    }, [id, soFilters]);

    useEffect(() => {
        fetchBuyer();
    }, [fetchBuyer]);

    useEffect(() => {
        if (buyer) {
            fetchSalesOrders();
        }
    }, [buyer, fetchSalesOrders]);

    if (loading) return <Layout><div className="text-center py-5"><PulseLoader color="#014b7cbb" size={9} /></div></Layout>;
    if (!buyer) return <Layout><p>Buyer not found.</p></Layout>;

    // If user can't view buyers, show permission denied
    if (!canViewBuyers) {
        return (
            <Layout>
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                        <li className="breadcrumb-item"><Link to="/buyers/">Buyers</Link></li>
                        <li className="breadcrumb-item active">Access Denied</li>
                    </ol>
                </nav>
                <Alert variant="danger" className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <div>
                        <strong>Access Denied</strong>
                        <p className="mb-0">You don't have permission to view buyer details. Contact your administrator for access.</p>
                    </div>
                </Alert>
            </Layout>
        );
    }

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                    <li className="breadcrumb-item"><Link to="/buyers/">Buyers</Link></li>
                    <li className="breadcrumb-item active">{buyer.business_name}</li>
                </ol>
            </nav>

            <div className="mb-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <h2 className="mb-0">Buyer Details</h2>
                {canEditBuyers && (
                    <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                        <i className="fas fa-edit me-2"></i>Edit Details
                    </button>
                )}
            </div>

            <div className="row g-5 ">
                {/* Left Column */}
                <div className="col-12 col-xxl-4 mt-0 px-0">
                    <div className="row g-3 h-100">
                        <div className="col-12 col-md-12 col-xl-7 col-xxl-12 px-0 pe-xl-2 ps-0">
                            <div className="card shadow-sm h-100">
                                <div className="card-body d-flex flex-column gap-3">
                                    {/* Header: Avatar & Basic Info */}
                                    <div className="d-flex flex-wrap align-items-center gap-3">
                                        <div className="avatar avatar-4xl flex-shrink-0">
                                            <img
                                                className="rounded-circle"
                                                src="/assets/img/team/default-avatar.png"
                                                alt="Buyer Avatar"
                                            />
                                        </div>
                                        <div className="flex-grow-1">
                                            <h3 className="mb-1">
                                                {buyer.business_name}
                                                {buyer.isInHouse && (
                                                    <span className="badge bg-success ms-2">
                                                        <i className="fas fa-building me-1"></i>In-House
                                                    </span>
                                                )}
                                            </h3>
                                            {buyer.alias && <h5 className="text-body-secondary mb-1">{buyer.alias}</h5>}
                                            <p className="mb-1 text-muted fs-85">
                                                <i className="fa fa-star text-warning"></i>
                                                <i className="fa fa-star text-warning"></i>
                                                <i className="fa fa-star text-warning"></i>
                                                <i className="fa fa-star text-warning"></i>
                                                <i className="fa-regular fa-star text-warning-light"></i>
                                            </p>
                                            <span className="badge bg-light text-secondary fs-85">
                                                Buyer Code: {buyer.buyer_code}
                                            </span>
                                        </div>
                                    </div>

                                    <hr className="my-1" />

                                    {/* Contact & Details */}
                                    <div className="row g-3">
                                        <div className="col-sm-6">
                                            <p className="text-body-secondary mb-1 fs-85">
                                                <i className="fa fa-user me-2 text-body-quaternary"></i>
                                                {buyer.owner_name || 'N/A'}
                                            </p>
                                            <p className="text-body-secondary mb-1 fs-85">
                                                <i className="fa fa-calendar me-2 text-body-quaternary"></i>
                                                Since{' '}
                                                {new Date(buyer.created_at).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </p>

                                            <p className="mb-1 fs-85">
                                                <Link to={`tel:${buyer.owner_contact_no}`} className="text-decoration-none text-dark">
                                                    <i className="fa fa-phone me-2 text-body-quaternary"></i>
                                                    {buyer.owner_contact_no}
                                                </Link>
                                            </p>


                                            <p className="mb-1 fs-85">
                                                <Link to={`mailto:${buyer.business_email}`} className="text-decoration-none text-dark">
                                                    <i className="fa fa-envelope me-2 text-body-quaternary"></i>
                                                    {buyer.business_email}
                                                </Link>
                                            </p>

                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">GSTIN: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.gst_registration === 'URD' || !buyer.gstin ? 'URD' : buyer.gstin}
                                                </div>
                                            </div>

                                        </div>

                                        <div className="col-sm-6">
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">Industry Type: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.industry_type?.name}
                                                </div>
                                            </div>
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">Firm Type: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.firm_type}
                                                </div>
                                            </div>
                                            <div className="fs-85 mb-2">
                                                <strong className="d-block mb-0 text-body">Address</strong>
                                                <div className="text-body-secondary">
                                                    {buyer.address && <div>{buyer.address}</div>}
                                                    <div>
                                                        {buyer?.town}, {buyer.city}, {buyer.state} {buyer.pin_code}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className="my-1" />
                                    <div className="row g-3">
                                        <div className="col-sm-6">

                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">PAN No.: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.pan_no}
                                                </div>
                                            </div>
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">TAN No.: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.tan_no}
                                                </div>
                                            </div>
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">Fssai No.: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.fssai_no}
                                                </div>
                                            </div>

                                        </div>

                                        <div className="col-sm-6">
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">Credit Period Allowed: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.credit_period_allowed}
                                                </div>
                                            </div>
                                            <div className="fs-85 d-flex gap-2 flex-wrap mb-2">
                                                <strong className="text-body">Other Services: </strong>
                                                <div className="text-body-secondary">
                                                    {buyer.other_services}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>

                        <div className="col-12 col-md-12 col-xl-5 col-xxl-12 px-0 ps-xl-2 pe-0">
                            <div className="card shadow-sm rounded-3 mb-4">
                                <div className="card-body d-flex flex-column  flex-md-row justify-content-between align-items-md-end gap-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <div>
                                            <h6 className="text-secondary fw-semibold mb-1">Status</h6>
                                            <span className={`badge rounded-pill bg-${getStatusColor(buyer.status)} px-2 py-1 fs-9`}>
                                                {buyer.status?.charAt(0).toUpperCase() + buyer.status?.slice(1) || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <h6 className="text-secondary fw-semibold mb-1">Verification</h6>
                                            <span className={`badge rounded-pill bg-${buyer.approved ? 'success' : 'danger'} px-2 py-1 fs-9`}>
                                                <i className={`fa ${buyer.approved ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
                                                {buyer.approved ? 'Approved' : 'Not Approved'}
                                            </span>
                                        </div>
                                    </div>

                                    {canEditBuyers && (
                                        <div className="text-md-end mt-2 mt-md-0">
                                            <button
                                                className="btn btn-sm btn-outline-primary px-3"
                                                onClick={() => setShowStatusModal(true)}
                                            >
                                                <i className="fa fa-edit me-1"></i> Edit
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>


                            <div className="card overflow-hidden">
                                <ul className="nav nav-tabs p-2 bg-primary-subtle" id="nav-tab" role="tablist">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'contacts' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('contacts')}
                                        >Contacts Persons</button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'bank' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('bank')}
                                        >Bank Details</button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'materials' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('materials')}
                                        >Products Bought</button>
                                    </li>
                                </ul>
                                <div className="card-body">


                                    <div className="tab-content">
                                        {activeTab === 'bank' && <BankDetailsTab bank_details={buyer.bank_details} onUpdated={fetchBuyer} buyerId={buyer._id} canEditBuyers={canEditBuyers} />}
                                        {activeTab === 'materials' && <MaterialsTab materials={buyer.material_supplied} onUpdated={fetchBuyer} buyerId={buyer._id} canEditBuyers={canEditBuyers} />}
                                        {activeTab === 'contacts' && <ContactsTab contacts={buyer.contact_persons} onUpdated={fetchBuyer} buyerId={buyer._id} canEditBuyers={canEditBuyers} />}
                                    </div>


                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Right Column (Sales Orders) */}
                <div className="col-12 col-xxl-8 px-0">
                    {/* Sales Orders Section */}
                    <div className="mb-6">
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Sales Orders</h3>
                                <small className="text-muted">Orders placed by this buyer</small>
                            </Card.Header>
                            <Card.Body>
                                {/* Filters */}
                                <Row className="mb-3 g-2">
                                    <Col md={2}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search SO Number"
                                            value={soFilters.soNumber}
                                            onChange={(e) => setSoFilters({...soFilters, soNumber: e.target.value, page: 1})}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search Broker Name"
                                            value={soFilters.brokerName}
                                            onChange={(e) => setSoFilters({...soFilters, brokerName: e.target.value, page: 1})}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Select
                                            value={soFilters.status}
                                            onChange={(e) => setSoFilters({...soFilters, status: e.target.value, page: 1})}
                                        >
                                            <option value="">All Status</option>
                                            <option value="Created">Created</option>
                                            <option value="Stock Pending">Stock Pending</option>
                                            <option value="Ready for Dispatch">Ready for Dispatch</option>
                                            <option value="Partially Dispatched">Partially Dispatched</option>
                                            <option value="Fully Dispatched">Fully Dispatched</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Control
                                            type="date"
                                            placeholder="Start Date"
                                            value={soFilters.startDate}
                                            onChange={(e) => setSoFilters({...soFilters, startDate: e.target.value, page: 1})}
                                        />
                                    </Col>
                                </Row>
                                <Row className="mb-3 g-2">
                                    <Col md={2}>
                                        <Form.Control
                                            type="date"
                                            placeholder="End Date"
                                            value={soFilters.endDate}
                                            onChange={(e) => setSoFilters({...soFilters, endDate: e.target.value, page: 1})}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Button variant="outline-secondary" onClick={() => {
                                            setSoFilters({page: 1, limit: 10, status: '', startDate: '', endDate: '', soNumber: '', brokerName: ''});
                                        }}>
                                            Clear Filters
                                        </Button>
                                    </Col>
                                </Row>

                                {soLoading ? (
                                    <div className="text-center py-4"><PulseLoader color="#014b7cbb" size={9} /></div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <Table striped bordered hover>
                                                <thead>
                                                    <tr>
                                                        <th>Order Number</th>
                                                        <th>Order Date</th>
                                                        <th>Items Ordered</th>
                                                        <th>Broker</th>
                                                        <th>Status</th>
                                                        <th>Total Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {salesOrders.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="6" className="text-center text-muted">
                                                                No sales orders found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        salesOrders.map((so) => (
                                                            <tr key={so._id}>
                                                                <td>
                                                                    <Link to={`/sales-orders/${so._id}`} className="text-decoration-none">
                                                                        {so.orderNumber}
                                                                    </Link>
                                                                </td>
                                                                <td>{new Date(so.orderDate).toLocaleDateString()}</td>
                                                                <td>
                                                                    {so.so_items && so.so_items.length > 0 ? (
                                                                        so.so_items.map((item, idx) => (
                                                                            <div key={idx} className="mb-1">
                                                                                <small className="text-primary fw-semibold">
                                                                                    {item.product?.productName || 'Unknown Product'}
                                                                                </small>
                                                                                <br />
                                                                                <small className="text-muted">
                                                                                    Qty: {item.quantity} {item.unit || item.product?.unit || ''}
                                                                                </small>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-muted">No items</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {so.broker ? (
                                                                        <Link to={`/brokers/${so.broker._id}`} className="text-decoration-none">
                                                                            {so.broker.business_name}
                                                                        </Link>
                                                                    ) : 'Direct'}
                                                                </td>
                                                                <td>
                                                                    <Badge bg={getSalesOrderStatusColor(so.status)}>
                                                                        {so.status}
                                                                    </Badge>
                                                                </td>
                                                                <td>₹{so.totalAmount?.toLocaleString() || 'N/A'}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Pagination */}
                                        {soPagination.totalPages > 1 && (
                                            <div className="d-flex justify-content-center mt-3">
                                                <Pagination>
                                                    <Pagination.First
                                                        disabled={soPagination.currentPage === 1}
                                                        onClick={() => setSoFilters({...soFilters, page: 1})}
                                                    />
                                                    <Pagination.Prev
                                                        disabled={soPagination.currentPage === 1}
                                                        onClick={() => setSoFilters({...soFilters, page: soPagination.currentPage - 1})}
                                                    />
                                                    {[...Array(soPagination.totalPages)].map((_, i) => (
                                                        <Pagination.Item
                                                            key={i + 1}
                                                            active={i + 1 === soPagination.currentPage}
                                                            onClick={() => setSoFilters({...soFilters, page: i + 1})}
                                                        >
                                                            {i + 1}
                                                        </Pagination.Item>
                                                    ))}
                                                    <Pagination.Next
                                                        disabled={soPagination.currentPage === soPagination.totalPages}
                                                        onClick={() => setSoFilters({...soFilters, page: soPagination.currentPage + 1})}
                                                    />
                                                    <Pagination.Last
                                                        disabled={soPagination.currentPage === soPagination.totalPages}
                                                        onClick={() => setSoFilters({...soFilters, page: soPagination.totalPages})}
                                                    />
                                                </Pagination>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </div>


                </div>
            </div>



            <StatusEditModal
                show={showStatusModal}
                onHide={() => setShowStatusModal(false)}
                buyerId={buyer._id}
                initialStatus={buyer.status}
                initialApproved={buyer.approved}
                onUpdated={fetchBuyer}
            />

            <BuyerEditModal
                show={showModal}
                onHide={() => setShowModal(false)}
                buyer={buyer}
                onBuyerUpdated={fetchBuyer}
            />

        </Layout>
    );
}

// Optional: Color-coded status badges
function getStatusColor(status) {
    switch (status) {
        case 'Active':
            return 'success';
        case 'OnHold':
            return 'warning';
        case 'Blocked':
            return 'danger';
        case 'Deactive':
            return 'secondary';
        default:
            return 'light';
    }
}

function getSalesOrderStatusColor(status) {
    switch (status) {
        case 'Created':
            return 'primary';
        case 'Stock Pending':
            return 'warning';
        case 'Production Requested':
            return 'info';
        case 'In Production':
            return 'secondary';
        case 'Filled From Production':
            return 'success';
        case 'Awaiting Receipt of PO':
            return 'warning';
        case 'PO Received':
            return 'info';
        case 'Ready for Dispatch':
            return 'primary';
        case 'Partially Dispatched':
            return 'warning';
        case 'Fully Dispatched':
            return 'success';
        default:
            return 'light';
    }
}


export default BuyerDetail;
